import { useEffect, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { Stack, tokens } from '@hotjar/hotsauce';
import { isEqual } from 'lodash-es';
import type { EmotionStyle } from '@hotjar/webapp/features/Feedback/domain/EmotionStyle';
import type {
  PageURLFilterValue,
  LargeListFilterValue,
  FilterType,
  GlobalFilter,
  KeywordResponseFilterValue,
  RadioTextFilterValue,
} from '@hotjar/global-filters';
import { RadioTextFilterOperation, PageURLFilter, FilterKindEnum } from '@hotjar/global-filters';
import { StatefulFilterChain } from '@hotjar/webapp/features/GlobalFilterChain/StatefulComponent/StatefulFilterChain';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import type {
  DeviceFilter,
  CountryFilter,
} from '@hotjar/global-filters/src/domains/global-filters/model/FilterChain';
import type { CountryCode } from '@hotjar/country-lib';
import { isAllResponsesEmailFilterValue } from './HasEmailFilter/HasEmailType';
import type { HasEmailFilterValue } from './HasEmailFilter/HasEmailType';
import { KeywordFilter } from '../../common/Responses/Filters/KeywordFilter/KeywordFilter';
import { isAllResponsesMessageFilterValue } from './HasFeedbackMessageFilter/HasFeedbackMessageType';
import type { HasMessageFilterValue } from './HasFeedbackMessageFilter/HasFeedbackMessageType';
import { HasEmailFilter } from './HasEmailFilter/HasEmailFilter';
import {
  DEFAULT_RATING_FILTER,
  IncomingFeedbackFilters,
} from '../../../domain/IncomingFeedback/filters/ResponsesFilters';
import type {
  CountryResponseFilter,
  DateResponseFilter,
  DeviceResponseFilter,
  HasEmailResponseFilter,
  HasMessageResponseFilter,
  KeywordResponseFilter,
  PageResponseFilter,
  RatingResponseFilter,
  ResponseFilter,
} from '../../../domain/IncomingFeedback/filters/ResponsesFilters';
import { areAllRatingsIncluded } from './RatingFilter/RatingFilterType';
import type { RatingFilterValue } from './RatingFilter/RatingFilterType';
import { HasFeedbackMessageFilter } from './HasFeedbackMessageFilter/HasFeedbackMessageFilter';
import { RatingFilter } from './RatingFilter/RatingFilter';

const Filters = styled.div`
  display: flex;
  gap: ${tokens.spacer.x2};
  flex-wrap: wrap;
`;

export interface FeedbackResponseFilters {
  [IncomingFeedbackFilters.DATE]: DateResponseFilter | undefined;
  [IncomingFeedbackFilters.DEVICE]: DeviceResponseFilter | undefined;
  [IncomingFeedbackFilters.COUNTRY]: CountryResponseFilter | undefined;
  [IncomingFeedbackFilters.PAGE]: PageResponseFilter | undefined;
  [IncomingFeedbackFilters.KEYWORD]: KeywordResponseFilter | undefined;
  [IncomingFeedbackFilters.RATING]: RatingResponseFilter | undefined;
  [IncomingFeedbackFilters.FEEDBACK_MESSAGE]: HasMessageResponseFilter | undefined;
  [IncomingFeedbackFilters.EMAIL]: HasEmailResponseFilter | undefined;
}

export interface ResponsesFiltersProps {
  onChange: (newFilerValue: ResponseFilter) => void;
  filters: Partial<FeedbackResponseFilters>;
  emotionStyle: EmotionStyle;
  onRemoveFilter: (filterType: IncomingFeedbackFilters) => void;
  hideRatingFilter?: boolean;
  onUpsellRequest: (featureName: string) => void;
}

function createGlobalFilter(filters: Partial<FeedbackResponseFilters>): GlobalFilter {
  function* generateGlobalFilterList(): Generator<FilterType> {
    {
      const deviceFilter = filters[IncomingFeedbackFilters.DEVICE];
      if (deviceFilter?.value) {
        yield {
          id: '1',
          kind: FilterKindEnum.DeviceType,
          value: deviceFilter.value,
        };
      }
    }
    {
      const countryFilter = filters[IncomingFeedbackFilters.COUNTRY];
      if (countryFilter?.value) {
        yield {
          id: '2',
          kind: FilterKindEnum.Country,
          value: countryFilter.value,
        };
      }
    }
  }

  return {
    dateRange: filters[IncomingFeedbackFilters.DATE]?.value,
    filterList: [...generateGlobalFilterList()],
  };
}

const getDeviceFilter = (globalFilter: GlobalFilter) =>
  globalFilter.filterList.find(
    (filter): filter is DeviceFilter => filter.kind === FilterKindEnum.DeviceType,
  )?.value;

const getCountryFilter = (globalFilter: GlobalFilter) =>
  globalFilter.filterList.find(
    (filter): filter is CountryFilter => filter.kind === FilterKindEnum.Country,
  )?.value;

const SUPPORTED_FILTER_LIST: Set<FilterKindEnum> = new Set([
  FilterKindEnum.Country,
  FilterKindEnum.DeviceType,
]);

export function ResponsesFilters({
  onChange,
  filters,
  emotionStyle,
  onRemoveFilter,
  onUpsellRequest,
  hideRatingFilter,
}: ResponsesFiltersProps): JSX.Element {
  const [globalFilter, setGlobalFilter] = useState<GlobalFilter>(createGlobalFilter(filters));
  const [urlFilterValue, setUrlFilterValue] = useState<PageURLFilterValue>();
  const [keywordFilterValue, setKeywordFilterValue] = useState<KeywordResponseFilterValue>();
  const [hasMessageFilterValue, setHasMessageFilterValue] = useState<HasMessageFilterValue>();
  const [hasEmailFilterValue, setHasEmailFilterValue] = useState<HasEmailFilterValue>();
  const [ratingFilterValue, setRatingFilterValue] = useState<RatingFilterValue>();

  useEffect(() => {
    setGlobalFilter(createGlobalFilter(filters));
    setUrlFilterValue(filters[IncomingFeedbackFilters.PAGE]?.value);
    setKeywordFilterValue(filters[IncomingFeedbackFilters.KEYWORD]?.value);
    setRatingFilterValue(filters[IncomingFeedbackFilters.RATING]?.value);
    setHasMessageFilterValue(filters[IncomingFeedbackFilters.FEEDBACK_MESSAGE]?.value);
    setHasEmailFilterValue(filters[IncomingFeedbackFilters.EMAIL]?.value);
  }, [filters]);

  const onGlobalFilterChange = useCallback(
    (newGlobalFilter: GlobalFilter) => {
      const newDeviceFilterValue = getDeviceFilter(newGlobalFilter);
      const oldDeviceFilterValue = getDeviceFilter(globalFilter);

      if (!isEqual(oldDeviceFilterValue, newDeviceFilterValue)) {
        onChange({
          type: IncomingFeedbackFilters.DEVICE,
          value: newDeviceFilterValue ?? [],
        });
      }

      const newCountryCodeFilterValue = getCountryFilter(newGlobalFilter);
      const oldCountryCodeFilterValue = getCountryFilter(globalFilter);

      if (
        oldCountryCodeFilterValue?.operation !== newCountryCodeFilterValue?.operation ||
        !isEqual(
          oldCountryCodeFilterValue?.selectedValues,
          newCountryCodeFilterValue?.selectedValues,
        )
      ) {
        onChange({
          type: IncomingFeedbackFilters.COUNTRY,
          value: newCountryCodeFilterValue as LargeListFilterValue<CountryCode>,
        });
      }

      const newDateRangeValue = newGlobalFilter.dateRange;
      if (globalFilter.dateRange !== newDateRangeValue) {
        onChange({
          type: IncomingFeedbackFilters.DATE,
          value: newDateRangeValue,
        });
      }
    },
    [onChange, globalFilter],
  );

  const onStatefulFilterChange = useCallback(
    (uiFilters: GlobalFilter) => {
      onGlobalFilterChange(uiFilters);
    },
    [onGlobalFilterChange],
  );

  return (
    <Stack direction="column">
      <Filters>
        <StatefulFilterChain
          currentFiltersInUiFormat={globalFilter}
          onChange={onStatefulFilterChange}
          supportedFilterList={SUPPORTED_FILTER_LIST}
          onUpsellRequest={onUpsellRequest}
          hideAllDateOption={false}
          hideSaveSegment
          contextOfUse={{
            productType: ProductType.Feedback,
            page: 'feedback.responses',
            toolName: 'Feedback',
          }}
        />
      </Filters>
      <Filters>
        <HasFeedbackMessageFilter
          currentValue={hasMessageFilterValue}
          onApply={(newValue) =>
            onChange({ type: IncomingFeedbackFilters.FEEDBACK_MESSAGE, value: newValue })
          }
          onRemove={
            isAllResponsesMessageFilterValue(hasMessageFilterValue)
              ? undefined
              : () => onRemoveFilter(IncomingFeedbackFilters.FEEDBACK_MESSAGE)
          }
          isInactive={isAllResponsesMessageFilterValue(hasMessageFilterValue)}
        />
        {hideRatingFilter !== true && (
          <RatingFilter
            emotionStyle={emotionStyle}
            currentValue={ratingFilterValue}
            onApply={(newValue) => {
              onChange({ value: newValue, type: IncomingFeedbackFilters.RATING });
            }}
            onRemove={
              ratingFilterValue && !areAllRatingsIncluded(ratingFilterValue)
                ? () => onChange({ ...DEFAULT_RATING_FILTER })
                : undefined
            }
            isInactive={!ratingFilterValue || areAllRatingsIncluded(ratingFilterValue)}
          />
        )}
        <PageURLFilter
          currentValue={urlFilterValue}
          onApply={(value: RadioTextFilterValue | undefined) => {
            switch (value?.operation) {
              case RadioTextFilterOperation.IS:
              case RadioTextFilterOperation.CONTAINS:
              case RadioTextFilterOperation.NOT_CONTAINS:
              case RadioTextFilterOperation.STARTS_WITH:
              case RadioTextFilterOperation.ENDS_WITH:
                onChange({ value, type: IncomingFeedbackFilters.PAGE });
                break;
              default:
                onChange({ value: undefined, type: IncomingFeedbackFilters.PAGE });
                break;
            }
          }}
          onRemove={urlFilterValue ? () => onRemoveFilter(IncomingFeedbackFilters.PAGE) : undefined}
          isInactive={!urlFilterValue || !urlFilterValue.operation}
        />
        <HasEmailFilter
          currentValue={hasEmailFilterValue}
          onApply={(newValue) => onChange({ type: IncomingFeedbackFilters.EMAIL, value: newValue })}
          onRemove={
            isAllResponsesEmailFilterValue(hasEmailFilterValue)
              ? undefined
              : () => onRemoveFilter(IncomingFeedbackFilters.EMAIL)
          }
          isInactive={isAllResponsesEmailFilterValue(hasEmailFilterValue)}
        />
        <KeywordFilter
          currentValue={keywordFilterValue}
          onApply={(newValue) =>
            onChange({ value: newValue, type: IncomingFeedbackFilters.KEYWORD })
          }
          onRemove={
            keywordFilterValue ? () => onRemoveFilter(IncomingFeedbackFilters.KEYWORD) : undefined
          }
          isInactive={!keywordFilterValue || !keywordFilterValue.operation}
        />
      </Filters>
    </Stack>
  );
}
